import React, { useState, useEffect } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Container,
  Typography,
  Grid,
  Card,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  styled,
  createTheme,
  ThemeProvider,
  Box,
  CardMedia,
  Button,
} from '@mui/material';
import moment from 'moment';
import Footer from '../navbar/Footer';

const enqueteSatisfactionData = [

  {
    "title": "Rapport d'enquête de satisfaction des utilisateurs des données statistiques officielles - 2023",
    "principalLink": "/documents/rapportSatisfaction/Rapport_enquete_satisfaction_ed_2023_2024_2v.pdf",
    "annexeLink": null,
    "image": "assets/enqueteSatisfaction/cover_satisfaction.webp",
    "description": "L’enquête 2023 montre que 73,08% des utilisateurs sont globalement satisfaits des statistiques produites par le SSN du Burundi, tout en appelant à une meilleure accessibilité, centralisation et disponibilité des données.",
    "date": "2024-12-10"
  },
  {
    "title": "Rapport d'enquête de satisfaction des utilisateurs des données statistiques officielles - 2021",
    "principalLink": "/documents/rapportSatisfaction/Rapport_satisfaction_2021_corrige_2v.pdf",
    "annexeLink": null,
    "image": "assets/enqueteSatisfaction/cover_satisfaction.webp",
    "description": "L’enquête 2021 sur la satisfaction des utilisateurs de statistiques au Burundi révèle que 75,8% des utilisateurs sont globalement satisfaits des données produites par le SSN, mais recommandent d'améliorer la diffusion, l’accessibilité, la régularité et la fiabilité des données pour mieux répondre à leurs besoins.",
    "date": "2021-12-10"
  }
];

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: '20px',
  backgroundColor: 'green',
  color: 'white',
  '&:hover': {
    backgroundColor: 'darkgreen',
  },
  '&:active': {
    backgroundColor: 'darkgreen',
  },
}));

function EnqueteSatisfaction() {
  const [visibleCount, setVisibleCount] = useState(4);
  const [selectedYear, setSelectedYear] = useState('Tous');
  const [filteredEnquetes, setFilteredEnquetes] = useState([]);

  const handleShowMore = () => {
    setVisibleCount((prevCount) => prevCount + 2);
  };

  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    if (year === 'Tous') {
      setFilteredEnquetes([...enqueteSatisfactionData].sort((a, b) => new Date(b.date) - new Date(a.date)));
    } else {
      const filtered = enqueteSatisfactionData.filter((rapport) => {
        const rapportYear = new Date(rapport.date).getFullYear();
        return rapportYear === parseInt(year);
      }).sort((a, b) => new Date(b.date) - new Date(a.date));
      setFilteredEnquetes(filtered);
    }
  };

  useEffect(() => {
    handleYearChange({ target: { value: 'Tous' } });
  }, []);

  const StyledSelect = styled(Select)(({ theme }) => ({
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.light,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.dark,
    },
  }));

  const theme = createTheme();

  return (
    <ThemeProvider theme={theme}>
      <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
        <NavigationBar />
        <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
          <Typography variant="h4" align="justify" gutterBottom style={{ color: 'green', marginBottom: '16px' }}>
            Enquête Périodique sur la Satisfaction des Utilisateurs des Données Statistiques Officielles
          </Typography>
          <Divider style={{ margin: '16px 0' }} />
          <Typography variant="body1" align="justify" paragraph style={{ marginBottom: '24px' }}>
            L'enquête périodique sur la satisfaction des utilisateurs des données statistiques officielles est une initiative majeure de l'Institut National de la Statistique du Burundi visant à évaluer et améliorer continuellement la qualité des services statistiques. Réalisée tous les deux ans, cette enquête mesure la satisfaction des différents acteurs (institutions publiques, partenaires techniques et financiers, chercheurs, secteur privé et grand public) concernant l'accessibilité, la pertinence, la qualité et la rapidité de diffusion des données statistiques produites.
          </Typography>

          <FormControl fullWidth variant="outlined" style={{ marginLeft: '0px', width: '300px' }}>
            <InputLabel id="year-select-label">Filtrer par année</InputLabel>
            <StyledSelect
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              onChange={handleYearChange}
              label="Filtrer par année"
            >
              <MenuItem value="Tous">Toutes les enquêtes</MenuItem>
              {[...new Set(enqueteSatisfactionData.map((rapport) => new Date(rapport.date).getFullYear()))]
                .sort((a, b) => b - a)
                .map((year) => (
                  <MenuItem key={year} value={year.toString()}>
                    {year}
                  </MenuItem>
                ))}
            </StyledSelect>
          </FormControl>

          <Grid container spacing={1} style={{ marginTop: '5px' }}>
            {filteredEnquetes.slice(0, visibleCount).map((rapport, index) => (
              <Grid item xs={12} sm={12} key={index}>
                <Card elevation={3} style={{ borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box sx={{ display: 'flex' }}>
                    <a
                      href={rapport.principalLink}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <CardMedia
                        component="img"
                        sx={{ width: 141, height: 150, m: 2 }}
                        image={rapport.image}
                        alt={rapport.title}
                      />
                    </a>
                    <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2, pt: 2 }}>
                      <Typography variant="h6" component="div" style={{ fontWeight: 'bold', marginBottom: '10px', color: 'green' }}>
                        <a
                          href={rapport.principalLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                          {<DownloadIcon />}
                          {rapport.title}
                        </a>
                      </Typography>
                      <Typography variant="body2" component="div" style={{ color: '#555', marginBottom: '10px' }}>
                        {rapport.description}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Publié en {moment(rapport.date).format('MMMM YYYY')}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {visibleCount < filteredEnquetes.length && (
            <StyledButton onClick={handleShowMore}>
              Plus d'enquêtes
            </StyledButton>
          )}
          <br />
          <br />
          <Typography variant="body1" align="justify" paragraph style={{ marginBottom: '24px', lineHeight: 1.6 }}>
            Les résultats de cette enquête guident les améliorations apportées aux produits et services statistiques du Système National de la Statistique.
          </Typography>
        </Container>
        <Footer />
      </div>
    </ThemeProvider>
  );
}

export default EnqueteSatisfaction;